import { InputFilterConfig } from 'components/SearchAndFilters/FilterTypes';
import SelectedFilterBadge from 'components/SearchAndFilters/SelectedFiltersBar/SelectedFilterBadge';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Input } from 'reactstrap';

/**
 * AgeFilterConfig class for Age Filter configuration
 * Value Pattern: { minValue: number, maxValue: number, isMinExclusive: boolean, isMaxExclusive: boolean }
 * @extends BaseFilter
 */
export interface AgeRangeValue {
  minValue?: number;
  maxValue?: number;
  isMinExclusive: boolean;
  isMaxExclusive: boolean;
}

export class AgeFilterConfig extends InputFilterConfig {
  render(onSelectedValueChange: () => void) {
    return (
      <AgeFilter
        config={this}
        onSelectedValueChange={(value) => {
          if (value) {
            this.currentSelectedValues = [value];
          } else {
            this.resetSelectionToDefault();
          }
          onSelectedValueChange();
        }}
      />
    );
  }

  private formatAgeRangeToStringArray(value: AgeRangeValue): string[] {
    const parts: string[] = [];
    if (value.minValue !== undefined) {
      parts.push(`min ${value?.isMinExclusive ? '>' : '>='} ${value.minValue}`);
    }
    if (value.maxValue !== undefined) {
      parts.push(`max ${value?.isMaxExclusive ? '<' : '<='} ${value.maxValue}`);
    }
    return [parts.join(', ')];
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const selectedValues = this.getSelectedValues();
    if (!selectedValues || selectedValues.length === 0) return null;

    const formattedValues = this.formatAgeRangeToStringArray(selectedValues[0]);
    return (
      <SelectedFilterBadge
        label={this.label}
        values={formattedValues}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}

export interface AgeFilterProps {
  config: AgeFilterConfig;
  onSelectedValueChange: (value: AgeRangeValue | null) => void;
}

const AgeFilter: React.FC<AgeFilterProps> = ({
  config,
  onSelectedValueChange,
}) => {
  // TO DO: UI for Age Filter
  // fix the querystring to match the backend
  // test the filter

  const [inputValue, setInputValue] = useState<AgeRangeValue>(
    config.getSelectedValues()?.[0] || {
      minValue: undefined,
      maxValue: undefined,
      isMinExclusive: false,
      isMaxExclusive: false,
    },
  );

  const debouncedUpdate = useCallback(
    debounce((newValue: AgeRangeValue | null) => {
      onSelectedValueChange(newValue);
    }, config.debounceTime),
    [config.debounceTime, onSelectedValueChange],
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'min' | 'max',
  ) => {
    const value = e.target.value ? parseInt(e.target.value) : undefined;
    setInputValue((prev) => {
      const newValue = {
        ...prev,
        [type === 'min' ? 'minValue' : 'maxValue']: value,
      };

      // If both min and max are undefined, reset the entire filter
      if (
        (type === 'min' && !value && !newValue.maxValue) ||
        (type === 'max' && !value && !newValue.minValue)
      ) {
        const resetValue = {
          minValue: undefined,
          maxValue: undefined,
          isMinExclusive: newValue.isMinExclusive,
          isMaxExclusive: newValue.isMaxExclusive,
        };

        if (config.enableDebounce) {
          debouncedUpdate(null);
        } else {
          onSelectedValueChange(null);
        }

        return resetValue;
      }

      // Only trigger API call if we have at least one value
      if (newValue.minValue !== undefined || newValue.maxValue !== undefined) {
        if (config.enableDebounce) {
          debouncedUpdate(newValue);
        } else {
          onSelectedValueChange(newValue);
        }
      }

      return newValue;
    });
  };

  const handleExclusiveChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'min' | 'max',
  ) => {
    const newValue = e.target.value === '1';
    setInputValue((prev) => {
      const updatedValue = {
        ...prev,
        [type === 'min' ? 'isMinExclusive' : 'isMaxExclusive']: newValue,
      };

      // If there's a value in the corresponding input, trigger API call
      if (
        (type === 'min' && updatedValue.minValue !== undefined) ||
        (type === 'max' && updatedValue.maxValue !== undefined)
      ) {
        if (config.enableDebounce) {
          debouncedUpdate(updatedValue);
        } else {
          onSelectedValueChange(updatedValue);
        }
      }

      return updatedValue;
    });
  };

  return (
    <>
      <div className="d-flex flex-row flex-fill flex-nowrap align-items-center border border-light rounded bg-light">
        <div
          className="text-nowrap align-items-center text-dark px-2 font-weight-bold"
          // style={labelWidth ? { width: labelWidth, maxWidth: labelWidth } : {}}
        >
          Min
        </div>
        <div className="flex-fill px-0 bg-white mr-0 border border-light rounded">
          <Input
            addon
            type="select"
            title={`>: Greater than\n>=: Greater than or equal to`}
            style={{
              color: 'black',
              border: '0',
              paddingLeft: '5px',
              paddingRight: '5px',
              WebkitAppearance: 'none',
              width: '18px',
              height: '36px',
              cursor: 'pointer',
            }}
            value={inputValue?.isMinExclusive ? 1 : 0}
            onChange={(e) => handleExclusiveChange(e, 'min')}
          >
            <option
              style={{
                color: 'black',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
              value={1}
            >
              &gt;
            </option>
            <option
              style={{
                color: 'black',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
              value={0}
            >
              &ge;
            </option>
          </Input>
        </div>
        <div className="flex-fill px-0 bg-white mr-1 border-0">
          <Input
            key="age_min"
            type="number"
            placeholder="min"
            value={inputValue?.minValue || ''}
            maxLength={4}
            onChange={(e) => handleChange(e, 'min')}
            className={`form-control  border-0`}
            style={{ backgroundColor: '#ffffff' }}
            width="100px"
          />
        </div>

        <div
          className="text-nowrap align-items-center text-dark px-2 font-weight-bold"
          // style={labelWidth ? { width: labelWidth, maxWidth: labelWidth } : {}}
        >
          Max
        </div>
        <div className="flex-fill px-0 bg-white mr-0 border border-light rounded">
          <Input
            addon
            type="select"
            title={`<: Less than\n<=: Less than or equal to`}
            style={{
              color: 'black',
              border: '0',
              paddingLeft: '5px',
              paddingRight: '5px',
              WebkitAppearance: 'none',
              width: '18px',
              height: '36px',
              cursor: 'pointer',
            }}
            value={inputValue?.isMaxExclusive ? 1 : 0}
            onChange={(e) => handleExclusiveChange(e, 'max')}
          >
            <option
              style={{
                color: 'black',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
              value={1}
            >
              &lt;
            </option>
            <option
              style={{
                color: 'black',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
              value={0}
            >
              &le;
            </option>
          </Input>
        </div>
        {/* Third column */}
        <div className="flex-fill bg-white px-0 border-0 rounded-right">
          <Input
            key="age_max"
            type="number"
            placeholder="max"
            value={inputValue?.maxValue || ''}
            maxLength={4}
            onChange={(e) => handleChange(e, 'max')}
            className={`form-control  border-0`}
            style={{ backgroundColor: '#ffffff' }}
            width="100px"
          />
        </div>
      </div>
    </>
  );
};

export default AgeFilter;
